import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import Tags from 'wanderu-component-tags';
import {
    CalendarIcon,
    ClockIcon,
    DollarIcon,
    TrainIcon,
    BusIcon,
    Table,
    TableHead,
    TableCell,
    TableRow,
    TableBody,
    Section,
    Typography,
    WDSButton,
} from 'wanderu-components';
import { localize } from 'wanderu-i18n';

import cityToCityConfig from '../../../../config/c2c.locale';
import events from './events';
import { formatDuration, getSearchUrls } from './utils';

// eslint-disable-next-line css-modules/no-unused-class
import styles from './AlternativeMode.scss';

const Tag = ({ t, id }) => {
    return (
        <Tags
            className={styles.tags}
            tags={[{ className: classNames(styles.tag, styles[id]), text: t(id) }]}
            disabled
        />
    );
};

Tag.propTypes = {
    id: PropTypes.string,
    t: PropTypes.func,
};

const BodyCell = ({ title, children }) => {
    return (
        <TableCell className={styles.cell}>
            {title && <Typography className={styles.hideLarge} variant="caption">{title}</Typography>}
            <Typography variant="h6">{children}</Typography>
        </TableCell>
    );
};

BodyCell.propTypes = {
    children: PropTypes.node,
    title: PropTypes.string,
};

const AlternativeMode = (props) => {
    const {
        data,
        departDate,
        history,
        i18nMoment,
        i18nMoney,
        locale,
        locationData,
        t,
    } = props;

    const onSearchClick = ({ url, type }) => {
        events.clickSearchEvent('train')
        history.push(url)
    }

    const headers = [
        {
            Icon: BusIcon,
            title: t('bus'),
        },
        {
            Icon: TrainIcon,
            title: t('train'),
        },
    ];

    const [busUrl, trainUrl] = getSearchUrls({ departDate, locale, locationData });

    const busDuration = formatDuration(data?.avg_bus_duration, i18nMoment, t);
    const trainDuration = formatDuration(data?.avg_train_duration, i18nMoment, t);
    const busPrice = i18nMoney(data?.avg_bus_price_usd).format();
    const trainPrice = i18nMoney(data?.avg_train_price_usd).format();

    if (!data?.avg_daily_bus_trips || !data?.avg_daily_train_trips) {
        return undefined;
    }

    return (
        <Section id="alternative-mode-section">
            <Typography
                className={styles.heading}
                component="h2"
                variant="h3"
            >
                {t('altModeTitle')}
            </Typography>
            <Typography paragraph>
                {t('altModeDescription')}
            </Typography>

            <Table variant="blocks">
                <TableHead>
                    <TableRow variant="head">
                        <TableCell
                            className={classNames(
                                styles.cell,
                                styles.hideSmall,
                                styles.labelColumn
                            )}
                            variant="head"
                        />
                        {headers.map(({ title, Icon }) => (
                            <Typography
                                component={TableCell}
                                componentProps={{
                                    className: classNames(styles.cell, styles.dataColumn),
                                    size: 'small',
                                    variant: 'head',
                                }}
                                key={title}
                            >
                                <div className={styles.flex}>
                                    {Icon && <Icon className={styles.icon} />}
                                    {title}
                                </div>
                            </Typography>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    <TableRow>
                        <TableCell className={classNames(styles.cell, styles.hideSmall)}>
                            <div className={styles.flex}><DollarIcon className={styles.icon} />{t('avgPrice')}</div>
                        </TableCell>
                        <BodyCell title={t('avgPrice')}>{busPrice}{data?.cheapest === 'bus' && <Tag id="cheaper" t={t} />}</BodyCell>
                        <BodyCell title={t('avgPrice')}>{trainPrice}{data?.cheapest === 'train' && <Tag id="cheaper" t={t} />}</BodyCell>
                    </TableRow>
                    <TableRow>
                        <TableCell className={classNames(styles.cell, styles.hideSmall)}>
                            <div className={styles.flex}><CalendarIcon className={styles.icon} />{t('dailyTrips')}</div>
                        </TableCell>
                        <BodyCell title={t('dailyTrips')}>{data?.avg_daily_bus_trips}</BodyCell>
                        <BodyCell title={t('dailyTrips')}>{data?.avg_daily_train_trips}</BodyCell>
                    </TableRow>
                    <TableRow>
                        <TableCell className={classNames(styles.cell, styles.hideSmall)}>
                            <div className={styles.flex}><ClockIcon className={styles.icon} />{t('duration')}</div>
                        </TableCell>
                        <BodyCell title={t('duration')}>{busDuration}{data?.fastest === 'bus' && <Tag id="faster" t={t} />}</BodyCell>
                        <BodyCell title={t('duration')}>{trainDuration}{data?.fastest === 'train' && <Tag id="faster" t={t} />}</BodyCell>
                    </TableRow>
                    <TableRow>
                        <TableCell className={classNames(styles.cell, styles.hideSmall)} />
                        <TableCell className={styles.cell}>
                            <WDSButton
                                className={styles.ctaButton}
                                color="secondary"
                                onClick={() => onSearchClick({
                                    url: busUrl,
                                    type: 'bus',
                                })}
                                variant="contained"
                                fullWidth
                            >
                                {t('searchBuses')}
                            </WDSButton>
                        </TableCell>
                        <TableCell className={styles.cell}>
                            <WDSButton
                                className={styles.ctaButton}
                                color="secondary"
                                onClick={() => onSearchClick({
                                    url: trainUrl,
                                    type: 'train',
                                })}
                                variant="outlined"
                                fullWidth
                            >
                                {t('searchTrains')}
                            </WDSButton>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </Section>
    );
};

AlternativeMode.defaultProps = {
    data: {},
    departDate: new Date(),
    locationData: {},
};

AlternativeMode.propTypes = {
    data: PropTypes.shape({
        avg_bus_duration: PropTypes.number,
        avg_bus_price_usd: PropTypes.number,
        avg_daily_bus_trips: PropTypes.number,
        avg_daily_train_trips: PropTypes.number,
        avg_train_duration: PropTypes.number,
        avg_train_price_usd: PropTypes.number,
        cheapest: PropTypes.oneOf(['bus', 'train']),
        fastest: PropTypes.oneOf(['bus', 'train']),
    }),
    departDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    history: PropTypes.object,
    i18nMoment: PropTypes.func,
    i18nMoney: PropTypes.func,
    locale: PropTypes.string,
    locationData: PropTypes.shape({
        arrival: PropTypes.shape({
            formattedAddress: PropTypes.string,
            placeId: PropTypes.string,
        }),
        departure: PropTypes.shape({
            formattedAddress: PropTypes.string,
            placeId: PropTypes.string,
        }),
    }),
    t: PropTypes.func,
};

export default localize(AlternativeMode, cityToCityConfig.namespace);
